import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { useAuth } from "@/hooks/use-auth";
import { resolveNextPath } from "@/lib/auth-redirect";
import { toast } from "sonner";

export const Route = createFileRoute("/auth/callback")({
  validateSearch: z.object({ next: z.string().optional() }).catch({}),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const { next } = Route.useSearch();

  useEffect(() => {
    if (!supabase) return;

    let cancelled = false;

    async function finish() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (cancelled) return;
        if (error) {
          toast.error(error.message);
          navigate({ to: "/login" });
          return;
        }
      } else {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error || !data.session) {
          toast.error(error?.message ?? "Sign-in could not be completed.");
          navigate({ to: "/login" });
          return;
        }
      }

      toast.success("Welcome to DealDraft");
      const { to, search } = resolveNextPath(next);
      navigate({ to: to as "/" | "/generate" | "/dashboard", search: search as never });
    }

    finish();
    return () => {
      cancelled = true;
    };
  }, [supabase, navigate, next]);

  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      Completing sign-in…
    </div>
  );
}
